import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { POPUP } from 'utils/constants/popup';
import Spinner from 'components/Spinner';
import { sidebarStatePropType } from 'components/Sidebar/propTypes';
import { useIsMobileResolution } from 'utils/hooks/useIsMobileResolution';
import { closePopups, pushPopup } from 'modules/popup/actions';
import { setFormFromProfile } from '../../actions';
import { selectOptionsFromServer } from '../../selectors';
import { completeGroupedOptions } from '../../functions/completeGroupedOptions';
import { getOptionsFromServer } from '../../functions/getOptionsFromServer';
import { useQuestionnaireData } from '../../context';
import QuestionOptionsContainer from '../QuestionOptionsContainer';
import { OptionsListWithSearch } from '../OptionsListWithSearch/OptionsListWithSearch';
import QuestionTitle from '../QuestionTitle';
import QuestionSubTitle from '../QuestionSubTitle';
import { FullScreenQuestionInput } from '../FullScreenQuestionInput';
import { SubPageSwitcher } from './SubPageSwitcher';
import { FILE_UPLOAD, SELECT_FROM_LIST } from './constants';
import { useStyles } from './biomarkersQuestionStyles';

export const BiomarkersQuestion = forwardRef(({
  question,
  sidebarState,
  value,
  onChange,
  shouldNextButtonBeShown,
  handleNextButtonClick,
  setAbilityToChangePrevBtnEvent,
}, ref) => {
  const { embedded } = useQuestionnaireData();
  const {
    id, title, placeholder,
  } = question;
  useEffect(() => () => {
    setAbilityToChangePrevBtnEvent(false);
  }, [setAbilityToChangePrevBtnEvent]);

  const dispatch = useDispatch();
  const [subPage, setSubPage] = useState();
  const [hideNoKnownMutation, setHideNoKnownMutation] = useState(false);
  const [titleOfSubPage, setTitleOfSubPage] = useState(title);
  const [subTitleOfSubPage, setSubTitleOfSubPage] = useState('');
  const [isFullScreenQuestionInputMode, setIsFullScreenQuestionInputMode] = useState(false);
  const {
    files, optionsFromServer, disease, biomarkers, provider,
  } = useSelector((state) => ({
    files: state?.questionnaire?.files,
    optionsFromServer: selectOptionsFromServer(state),
    disease: state?.profile?.data?.personal?.condition,
    biomarkers: state?.profile?.data?.condition_profile?.biomarkers,
    provider: state?.appConfiguration?.providerSpecific?.code,
  }));
  const [optionsFromServerNew, setOptionsFromServerNew] = useState({});

  const isMobile = useIsMobileResolution();

  useEffect(() => {
    getOptionsFromServer(disease, question, optionsFromServer)
      .then((data) => setOptionsFromServerNew(data))
      .catch((error) => console.error(error));
  }, [disease, optionsFromServer, question]);

  const classes = useStyles({ isInsideSidebar: !!sidebarState, provider, embedded });

  useImperativeHandle(ref, () => ({
    prevBtnEvent() {
      if (subPage !== '') {
        // return to 'select method' subpage
        setTitleOfSubPage(title);
        setSubPage('');
        setAbilityToChangePrevBtnEvent(false);
      }
    },
  }));

  const handleSubPageChange = (newValue) => {
    if (newValue === FILE_UPLOAD) {
      dispatch(pushPopup({
        type: POPUP.UPLOAD,
        data: {
          theme: 'middle',
          preventOverlayClose: true,
          condition: disease,
          presentNgsNotFound: true,
          onSuccess: async (user) => {
            await dispatch(setFormFromProfile(user));

            onChange(
              user.condition_profile.biomarkers.map((biomarker) => ({
                value: biomarker,
                label: `questionnaire.questions.biomarkers.option.${biomarker}`,
              })),
            );

            // TODO TBD or close active popup
            dispatch(closePopups());
            handleNextButtonClick();
          },
        },
      }));
      return;
    }

    setAbilityToChangePrevBtnEvent(true);
    setSubPage(newValue);
  };

  const handleBackButtonClick = () => {
    setHideNoKnownMutation(false);
    setIsFullScreenQuestionInputMode(false);
  };

  const handleOptionsListSearchInputFocus = useCallback(() => {
    if (isMobile) {
      setHideNoKnownMutation(true);
      setIsFullScreenQuestionInputMode(true);
    }
  }, [isMobile]);

  const {
    groupedOptions,
    groupedOptionsByDiseases,
    topSelectedOptionsByDiseases,
    options: { dropDownOptions, radioOptions } = {},
  } = optionsFromServerNew ?? {};

  // to prevent nested component logic recalculation
  const completedGroupedOptions = useMemo(
    () => completeGroupedOptions({
      groupedOptions: groupedOptions ?? groupedOptionsByDiseases?.[disease],
      topSelectedOptions: topSelectedOptionsByDiseases?.[disease],
    }),
    [disease, groupedOptions, groupedOptionsByDiseases, topSelectedOptionsByDiseases],
  );

  const handleSkipButtonClick = () => {
    const noneOption = radioOptions.filter((option) => option.value === 'none');
    onChange(noneOption);
    handleNextButtonClick();
  };

  const displayParts = ({ optionsListWithSearchClassName, autoFocus = false }) => {
    switch (subPage) {
      case SELECT_FROM_LIST:
        return dropDownOptions?.length ? (
          <OptionsListWithSearch
            isMultipleSelection
            id={id}
            values={value ?? []}
            onChange={onChange}
            searchPlaceholder={placeholder}
            fixedOptions={radioOptions}
            options={dropDownOptions}
            groupedOptions={completedGroupedOptions}
            className={optionsListWithSearchClassName}
            onSearchInputFocus={handleOptionsListSearchInputFocus}
            hideNoKnownMutation={hideNoKnownMutation}
            autoFocus={autoFocus}
          />
        ) : (
          <Spinner />
        );
      default:
        return (
          <SubPageSwitcher
            handleNext={handleSkipButtonClick}
            onSubPageChange={handleSubPageChange}
            setTitle={setTitleOfSubPage}
            setSubTitle={setSubTitleOfSubPage}
            isMobile={isMobile}
            hideGeneticSkipBtn={biomarkers?.length > 0}
          />
        );
    }
  };

  const renderQuestionInput = ({ optionsListWithSearchClassName, autoFocus } = {}) => displayParts({ optionsListWithSearchClassName, autoFocus });

  return (
    <>
      {isFullScreenQuestionInputMode && (
        <FullScreenQuestionInput
          shouldNextButtonBeShown={shouldNextButtonBeShown}
          handleNext={handleNextButtonClick}
          handleBack={handleBackButtonClick}
        >
          {renderQuestionInput({ autoFocus: true })}
        </FullScreenQuestionInput>
      )}
      <div className={classes.root}>
        {!files[id]?.length && <QuestionTitle label={titleOfSubPage} />}
        {!isMobile && !files[id]?.length && <QuestionSubTitle label={subTitleOfSubPage} />}

        <QuestionOptionsContainer isFullWidth>
          <div className={classes.questionInput}>
            {!isFullScreenQuestionInputMode
              && renderQuestionInput({
                optionsListWithSearchClassName: classes.optionsListWithSearch,
              })}
          </div>
        </QuestionOptionsContainer>
      </div>
    </>
  );
});

BiomarkersQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  sidebarState: sidebarStatePropType,
  onChange: PropTypes.func.isRequired,
  setAbilityToChangePrevBtnEvent: PropTypes.func.isRequired,
  value: PropTypes.array,
  shouldNextButtonBeShown: PropTypes.bool.isRequired,
  handleNextButtonClick: PropTypes.func.isRequired,
};
